import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { FileText, Calculator, Eye, RotateCcw } from 'lucide-react';
import { Quotation } from './Quotation';
import { getAdminDisplayName } from '../utils/adminNames';

const DEFAULT_KM_PER_DAY = 300;

const buildQuotationNumber = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  // 4-digit random-ish suffix from time
  const suffix = String(d.getHours()).padStart(2, '0') + String(d.getMinutes()).padStart(2, '0');
  return `QT-${yyyy}${mm}${dd}-${suffix}`;
};

export const QuotationPage = ({ user }) => {
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    routeName: '',
    travelDate: '',
    vehicleType: 'Sedan',
    days: 2,
    kmPerDay: DEFAULT_KM_PER_DAY,
    perKmRate: 18,
    driverAllowance: '',
    permit: '',
    toll: '',
    parking: '',
    extraNotes: '',
  });
  const [showPreview, setShowPreview] = useState(false);
  const [quotationNumber, setQuotationNumber] = useState(buildQuotationNumber());

  const baseFare = useMemo(() => {
    const d = Number(form.days) || 0;
    const k = Number(form.kmPerDay) || 0;
    const r = Number(form.perKmRate) || 0;
    return d * k * r;
  }, [form.days, form.kmPerDay, form.perKmRate]);

  const totalEstimate = useMemo(() => {
    return (
      baseFare +
      (Number(form.driverAllowance) || 0) +
      (Number(form.permit) || 0) +
      (Number(form.toll) || 0) +
      (Number(form.parking) || 0)
    );
  }, [baseFare, form.driverAllowance, form.permit, form.toll, form.parking]);

  const handleChange = (key) => (e) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleReset = () => {
    setForm({
      customerName: '',
      customerPhone: '',
      routeName: '',
      travelDate: '',
      vehicleType: 'Sedan',
      days: 2,
      kmPerDay: DEFAULT_KM_PER_DAY,
      perKmRate: 18,
      driverAllowance: '',
      permit: '',
      toll: '',
      parking: '',
      extraNotes: '',
    });
    setQuotationNumber(buildQuotationNumber());
  };

  const handleGenerate = () => {
    setQuotationNumber(buildQuotationNumber());
    setShowPreview(true);
  };

  const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const quotationData = useMemo(() => ({
    quotationNumber,
    customerName: form.customerName,
    customerPhone: form.customerPhone,
    routeName: form.routeName,
    travelDate: form.travelDate,
    vehicleType: form.vehicleType,
    days: form.days,
    kmPerDay: form.kmPerDay,
    perKmRate: form.perKmRate,
    baseFare,
    driverAllowance: form.driverAllowance,
    permit: form.permit,
    toll: form.toll,
    parking: form.parking,
    extraNotes: form.extraNotes,
    totalEstimate,
    createdBy: getAdminDisplayName(user),
  }), [quotationNumber, form, baseFare, totalEstimate, user]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <FileText className="w-6 h-6 mr-2 text-yellow-600" />
          Rough Quotation
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Generate a branded trip quotation and share it instantly with the customer.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-4">
          <Card data-testid="quotation-customer-card">
            <CardHeader>
              <CardTitle className="text-base">Customer & Trip</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customerName">Customer Name *</Label>
                  <Input
                    id="customerName"
                    data-testid="quotation-customer-name"
                    value={form.customerName}
                    onChange={handleChange('customerName')}
                    placeholder="e.g. Rahul Sharma"
                  />
                </div>
                <div>
                  <Label htmlFor="customerPhone">Customer Phone *</Label>
                  <Input
                    id="customerPhone"
                    data-testid="quotation-customer-phone"
                    value={form.customerPhone}
                    onChange={handleChange('customerPhone')}
                    placeholder="e.g. 9876543210"
                    inputMode="tel"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="routeName">Route / Destination</Label>
                  <Input
                    id="routeName"
                    data-testid="quotation-route"
                    value={form.routeName}
                    onChange={handleChange('routeName')}
                    placeholder="e.g. Bangalore → Coorg → Mysuru"
                  />
                </div>
                <div>
                  <Label htmlFor="travelDate">Travel Date</Label>
                  <Input
                    id="travelDate"
                    data-testid="quotation-travel-date"
                    type="date"
                    value={form.travelDate}
                    onChange={handleChange('travelDate')}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="vehicleType">Vehicle Type</Label>
                <select
                  id="vehicleType"
                  data-testid="quotation-vehicle-type"
                  value={form.vehicleType}
                  onChange={handleChange('vehicleType')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="Sedan">Sedan</option>
                  <option value="SUV">SUV</option>
                  <option value="Tempo Traveller">Tempo Traveller</option>
                </select>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="quotation-fare-card">
            <CardHeader>
              <CardTitle className="text-base flex items-center">
                <Calculator className="w-4 h-4 mr-2" />
                Fare Inputs
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="days">Days *</Label>
                  <Input
                    id="days"
                    data-testid="quotation-days"
                    type="number"
                    min="1"
                    value={form.days}
                    onChange={handleChange('days')}
                  />
                </div>
                <div>
                  <Label htmlFor="kmPerDay">Km / Day *</Label>
                  <Input
                    id="kmPerDay"
                    data-testid="quotation-km-per-day"
                    type="number"
                    min="0"
                    value={form.kmPerDay}
                    onChange={handleChange('kmPerDay')}
                  />
                </div>
                <div>
                  <Label htmlFor="perKmRate">₹ / Km *</Label>
                  <Input
                    id="perKmRate"
                    data-testid="quotation-per-km-rate"
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.perKmRate}
                    onChange={handleChange('perKmRate')}
                  />
                </div>
              </div>
              <div className="rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 flex justify-between items-center">
                <div>
                  <p className="text-xs uppercase tracking-wide text-yellow-800 font-semibold">Base Fare (Fixed)</p>
                  <p className="text-xs text-gray-600 mt-1">
                    {form.kmPerDay || 0} km × {form.days || 0} day(s) × ₹{form.perKmRate || 0}/km
                  </p>
                </div>
                <p className="text-xl font-bold text-gray-900" data-testid="quotation-base-fare-preview">
                  {formatINR(baseFare)}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="driverAllowance">Driver Allowance</Label>
                  <Input
                    id="driverAllowance"
                    data-testid="quotation-driver-allowance"
                    type="number"
                    min="0"
                    value={form.driverAllowance}
                    onChange={handleChange('driverAllowance')}
                    placeholder="e.g. 800"
                  />
                </div>
                <div>
                  <Label htmlFor="permit">Permit (if applicable)</Label>
                  <Input
                    id="permit"
                    data-testid="quotation-permit"
                    type="number"
                    min="0"
                    value={form.permit}
                    onChange={handleChange('permit')}
                    placeholder="e.g. 500"
                  />
                </div>
                <div>
                  <Label htmlFor="toll">Toll (if applicable)</Label>
                  <Input
                    id="toll"
                    data-testid="quotation-toll"
                    type="number"
                    min="0"
                    value={form.toll}
                    onChange={handleChange('toll')}
                    placeholder="e.g. 300"
                  />
                </div>
                <div>
                  <Label htmlFor="parking">Parking (if applicable)</Label>
                  <Input
                    id="parking"
                    data-testid="quotation-parking"
                    type="number"
                    min="0"
                    value={form.parking}
                    onChange={handleChange('parking')}
                    placeholder="e.g. 100"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="extraNotes">Additional Notes (optional)</Label>
                <Textarea
                  id="extraNotes"
                  data-testid="quotation-extra-notes"
                  value={form.extraNotes}
                  onChange={handleChange('extraNotes')}
                  placeholder="Any inclusions / exclusions you'd like to mention…"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary */}
        <div>
          <Card className="lg:sticky lg:top-6" data-testid="quotation-summary-card">
            <CardHeader>
              <CardTitle className="text-base">Quotation Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Base Fare</span>
                  <span className="font-medium text-gray-900">{formatINR(baseFare)}</span>
                </div>
                {Number(form.driverAllowance) > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Driver Allowance</span>
                    <span className="font-medium text-gray-900">{formatINR(form.driverAllowance)}</span>
                  </div>
                ) : null}
                {Number(form.permit) > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Permit</span>
                    <span className="font-medium text-gray-900">{formatINR(form.permit)}</span>
                  </div>
                ) : null}
                {Number(form.toll) > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Toll (est.)</span>
                    <span className="font-medium text-gray-900">{formatINR(form.toll)}</span>
                  </div>
                ) : null}
                {Number(form.parking) > 0 ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Parking (est.)</span>
                    <span className="font-medium text-gray-900">{formatINR(form.parking)}</span>
                  </div>
                ) : null}
              </div>
              <div className="border-t border-gray-200 mt-4 pt-3 flex justify-between items-center">
                <span className="text-sm font-semibold text-gray-700">Estimated Total</span>
                <span className="text-2xl font-extrabold text-gray-900" data-testid="quotation-total-preview">
                  {formatINR(totalEstimate)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-2 italic">
                Estimate — only the base fare is fixed.
              </p>

              <div className="flex flex-col gap-2 mt-5">
                <Button
                  onClick={handleGenerate}
                  data-testid="quotation-generate-btn"
                  disabled={!form.customerName || !form.customerPhone || baseFare <= 0}
                  className="w-full bg-black hover:bg-gray-900 text-yellow-400 font-semibold"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Generate & Preview
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  data-testid="quotation-reset-btn"
                  className="w-full"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {showPreview ? (
        <Quotation
          onClose={() => setShowPreview(false)}
          data={quotationData}
        />
      ) : null}
    </div>
  );
};
